/**
 * Milestone definitions
 * Achievements that track player progress and award rewards.
 */

import {
  MilestoneDefinition,
  MilestoneCategory,
  MilestoneRewardType,
  Quality,
  PlayerState,
} from '@/types';
import { getZoneDefinition } from '@/data/zones/zoneData';

// ============================================================
// Milestone Definitions
// ============================================================

export const MILESTONE_DEFINITIONS: MilestoneDefinition[] = [
  // === COLLECTION MILESTONES ===
  {
    id: 'first_discovery',
    name: 'First Discovery',
    description: 'Discover 1 fish species.',
    category: MilestoneCategory.COLLECTION,
    icon: '🔍',
    rewards: [{ type: MilestoneRewardType.CURRENCY, value: 25, description: '+25g' }],
    conditionKey: 'first_discovery',
  },
  {
    id: 'amateur_naturalist',
    name: 'Amateur Naturalist',
    description: 'Discover 5 fish species.',
    category: MilestoneCategory.COLLECTION,
    icon: '📖',
    rewards: [{ type: MilestoneRewardType.CURRENCY, value: 100, description: '+100g' }],
    conditionKey: 'amateur_naturalist',
  },
  {
    id: 'master_naturalist',
    name: 'Master Naturalist',
    description: 'Discover all fish species in Mountain Lake.',
    category: MilestoneCategory.COLLECTION,
    icon: '🏅',
    rewards: [
      { type: MilestoneRewardType.CURRENCY, value: 500, description: '+500g' },
      { type: MilestoneRewardType.INVENTORY_EXPANSION, value: 10, description: '+10 inventory' },
    ],
    conditionKey: 'master_naturalist',
  },
  {
    id: 'quality_hunter',
    name: 'Quality Hunter',
    description: 'Catch a fish of each quality (Common through Legendary).',
    category: MilestoneCategory.COLLECTION,
    icon: '🌈',
    rewards: [{ type: MilestoneRewardType.CURRENCY, value: 300, description: '+300g' }],
    conditionKey: 'quality_hunter',
  },
  {
    id: 'perfect_specimen',
    name: 'Perfect Specimen',
    description: 'Catch a UNIQUE quality fish.',
    category: MilestoneCategory.COLLECTION,
    icon: '⭐',
    rewards: [{ type: MilestoneRewardType.CURRENCY, value: 200, description: '+200g' }],
    conditionKey: 'perfect_specimen',
  },

  // === ECONOMY MILESTONES ===
  {
    id: 'first_sale',
    name: 'First Sale',
    description: 'Sell 1 fish.',
    category: MilestoneCategory.ECONOMY,
    icon: '🪙',
    rewards: [{ type: MilestoneRewardType.CURRENCY, value: 10, description: '+10g' }],
    conditionKey: 'first_sale',
  },
  {
    id: 'merchant',
    name: 'Merchant',
    description: 'Earn 1,000g total.',
    category: MilestoneCategory.ECONOMY,
    icon: '💰',
    rewards: [{ type: MilestoneRewardType.CURRENCY, value: 100, description: '+100g' }],
    conditionKey: 'merchant',
  },
  {
    id: 'tycoon',
    name: 'Tycoon',
    description: 'Earn 10,000g total.',
    category: MilestoneCategory.ECONOMY,
    icon: '💎',
    rewards: [
      { type: MilestoneRewardType.CURRENCY, value: 500, description: '+500g' },
      { type: MilestoneRewardType.INVENTORY_EXPANSION, value: 10, description: '+10 inventory' },
    ],
    conditionKey: 'tycoon',
  },
  {
    id: 'fish_baron',
    name: 'Fish Baron',
    description: 'Earn 100,000g total.',
    category: MilestoneCategory.ECONOMY,
    icon: '👑',
    rewards: [{ type: MilestoneRewardType.CURRENCY, value: 2000, description: '+2,000g' }],
    conditionKey: 'fish_baron',
  },

  // === FISHING MILESTONES ===
  {
    id: 'novice',
    name: 'Novice',
    description: 'Catch 10 fish.',
    category: MilestoneCategory.FISHING,
    icon: '🐟',
    rewards: [{ type: MilestoneRewardType.CURRENCY, value: 50, description: '+50g' }],
    conditionKey: 'novice',
  },
  {
    id: 'experienced',
    name: 'Experienced',
    description: 'Catch 100 fish.',
    category: MilestoneCategory.FISHING,
    icon: '🎣',
    rewards: [{ type: MilestoneRewardType.CURRENCY, value: 200, description: '+200g' }],
    conditionKey: 'experienced',
  },
  {
    id: 'expert',
    name: 'Expert',
    description: 'Catch 500 fish.',
    category: MilestoneCategory.FISHING,
    icon: '🏆',
    rewards: [{ type: MilestoneRewardType.CURRENCY, value: 500, description: '+500g' }],
    conditionKey: 'expert',
  },
  {
    id: 'master_angler',
    name: 'Master Angler',
    description: 'Catch 1,000 fish.',
    category: MilestoneCategory.FISHING,
    icon: '🥇',
    rewards: [
      { type: MilestoneRewardType.CURRENCY, value: 1000, description: '+1,000g' },
      { type: MilestoneRewardType.INVENTORY_EXPANSION, value: 20, description: '+20 inventory' },
    ],
    conditionKey: 'master_angler',
  },

  // === BUDDY MILESTONES ===
  {
    id: 'first_hire',
    name: 'First Hire',
    description: 'Hire 1 buddy.',
    category: MilestoneCategory.BUDDY,
    icon: '🤝',
    rewards: [{ type: MilestoneRewardType.CURRENCY, value: 50, description: '+50g' }],
    conditionKey: 'first_hire',
  },
  {
    id: 'full_crew',
    name: 'Full Crew',
    description: 'Hire all 3 fishing buddies.',
    category: MilestoneCategory.BUDDY,
    icon: '👥',
    rewards: [{ type: MilestoneRewardType.CURRENCY, value: 200, description: '+200g' }],
    conditionKey: 'full_crew',
  },
  {
    id: 'automation',
    name: 'Automation',
    description: 'Have a hauler complete 10 trips.',
    category: MilestoneCategory.BUDDY,
    icon: '🚚',
    rewards: [{ type: MilestoneRewardType.CURRENCY, value: 300, description: '+300g' }],
    conditionKey: 'automation',
  },

  // === STREAK MILESTONES ===
  {
    id: 'hot_streak',
    name: 'Hot Streak',
    description: 'Catch 5 fish in a row without a miss.',
    category: MilestoneCategory.STREAK,
    icon: '🔥',
    rewards: [{ type: MilestoneRewardType.CURRENCY, value: 50, description: '+50g' }],
    conditionKey: 'hot_streak',
  },
  {
    id: 'on_fire',
    name: 'On Fire',
    description: 'Catch 10 fish in a row without a miss.',
    category: MilestoneCategory.STREAK,
    icon: '🔥',
    rewards: [{ type: MilestoneRewardType.CURRENCY, value: 150, description: '+150g' }],
    conditionKey: 'on_fire',
  },
  {
    id: 'unstoppable',
    name: 'Unstoppable',
    description: 'Catch 20 fish in a row without a miss.',
    category: MilestoneCategory.STREAK,
    icon: '⚡',
    rewards: [{ type: MilestoneRewardType.CURRENCY, value: 500, description: '+500g' }],
    conditionKey: 'unstoppable',
  },

  // === TRAPPING MILESTONES ===
  {
    id: 'milestone_first_trap_pull',
    name: 'First Haul',
    description: 'Pull your first trap.',
    category: MilestoneCategory.TRAPPING,
    icon: '🪤',
    rewards: [{ type: MilestoneRewardType.CURRENCY, value: 25, description: '+25g' }],
    conditionKey: 'first_trap_pull',
  },
  {
    id: 'milestone_10_trap_pulls',
    name: 'Trap Enthusiast',
    description: 'Pull 10 traps.',
    category: MilestoneCategory.TRAPPING,
    icon: '🪤',
    rewards: [{ type: MilestoneRewardType.CURRENCY, value: 100, description: '+100g' }],
    conditionKey: 'trap_enthusiast',
  },
  {
    id: 'milestone_50_creatures',
    name: 'Crustacean Collector',
    description: 'Catch 50 creatures from traps.',
    category: MilestoneCategory.TRAPPING,
    icon: '🦞',
    rewards: [{ type: MilestoneRewardType.CURRENCY, value: 250, description: '+250g' }],
    conditionKey: 'crustacean_collector',
  },
];

// ============================================================
// Condition Registry
// ============================================================

/**
 * Registry of condition-checking functions.
 * Each function takes PlayerState + optional transient data and returns boolean.
 */
export type MilestoneConditionFn = (
  playerState: PlayerState,
  transient: MilestoneTransientData,
) => boolean;

/**
 * Transient data not persisted in PlayerState but needed for some milestone checks.
 * Passed in by the MilestoneManager.
 */
export interface MilestoneTransientData {
  currentStreak: number;
  haulerTripsCompleted: number;
}

export const MILESTONE_CONDITIONS: Record<string, MilestoneConditionFn> = {
  // Collection
  first_discovery: (ps) => ps.discoveredFish.length >= 1,
  amateur_naturalist: (ps) => ps.discoveredFish.length >= 5,
  master_naturalist: (ps) => {
    const zone = getZoneDefinition(ps.currentZone);
    const discoveredIds = new Set(ps.discoveredFish.map((d) => d.definitionId));
    return zone.fishPool.every((fishId) => discoveredIds.has(fishId));
  },
  quality_hunter: (ps) => {
    const requiredQualities = [
      Quality.COMMON,
      Quality.UNCOMMON,
      Quality.RARE,
      Quality.EPIC,
      Quality.LEGENDARY,
    ];
    return requiredQualities.every(
      (q) => (ps.stats.fishCaughtByQuality[q] ?? 0) > 0,
    );
  },
  perfect_specimen: (ps) => (ps.stats.fishCaughtByQuality[Quality.UNIQUE] ?? 0) > 0,

  // Economy
  first_sale: (ps) => ps.stats.totalFishSold >= 1,
  merchant: (ps) => ps.stats.totalCurrencyEarned >= 1000,
  tycoon: (ps) => ps.stats.totalCurrencyEarned >= 10000,
  fish_baron: (ps) => ps.stats.totalCurrencyEarned >= 100000,

  // Fishing
  novice: (ps) => ps.stats.totalFishCaught >= 10,
  experienced: (ps) => ps.stats.totalFishCaught >= 100,
  expert: (ps) => ps.stats.totalFishCaught >= 500,
  master_angler: (ps) => ps.stats.totalFishCaught >= 1000,

  // Buddy
  first_hire: (ps) => ps.fishingBuddies.length >= 1 || ps.haulerBuddies.length >= 1,
  full_crew: (ps) => ps.fishingBuddies.length >= 3,
  automation: (_ps, transient) => transient.haulerTripsCompleted >= 10,

  // Streak (uses transient data)
  hot_streak: (_ps, transient) => transient.currentStreak >= 5,
  on_fire: (_ps, transient) => transient.currentStreak >= 10,
  unstoppable: (_ps, transient) => transient.currentStreak >= 20,

  // Trapping
  first_trap_pull: (ps) => ps.trapState.totalTrapsPulled >= 1,
  trap_enthusiast: (ps) => ps.trapState.totalTrapsPulled >= 10,
  crustacean_collector: (ps) => ps.trapState.totalCreaturesCaught >= 50,
};
